import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { ApiResponse } from '@painter-crm/shared';

function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0))) as BufferSource;
}

async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export function usePushSubscriptionStatus() {
  return useQuery({
    queryKey: ['push', 'subscription-status'],
    queryFn: async () => {
      const subscription = await getExistingSubscription();
      return { subscribed: subscription !== null };
    },
    enabled: 'serviceWorker' in navigator && 'PushManager' in window,
  });
}

export function useEnablePush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Push notifications are not supported on this device');
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission was not granted');
      }

      const { data } = await api.get<ApiResponse<{ publicKey: string }>>('/api/v1/push/vapid-public-key');
      const publicKey = data.data?.publicKey;
      if (!publicKey) {
        throw new Error('Push notifications are not configured on the server');
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const json = subscription.toJSON();
      await api.post('/api/v1/push/subscriptions', {
        endpoint: json.endpoint,
        keys: json.keys,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push', 'subscription-status'] });
    },
  });
}

async function subscribeAndRegister(): Promise<void> {
  const { data } = await api.get<ApiResponse<{ publicKey: string }>>('/api/v1/push/vapid-public-key');
  const publicKey = data.data?.publicKey;
  if (!publicKey) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  await registerSubscription(subscription.toJSON());
}

async function registerSubscription(json: { endpoint?: string; keys?: { p256dh?: string; auth?: string } }): Promise<void> {
  if (!json.endpoint || !json.keys) return;
  await api.post('/api/v1/push/subscriptions', { endpoint: json.endpoint, keys: json.keys });
}

/**
 * Self-heals a stale or lost push subscription without prompting the user
 * again. Call on app load and whenever the tab regains focus — cheap
 * (only touches the network when permission is already granted) and covers
 * the two ways a device silently stops receiving pushes: the browser
 * dropped/rotated the subscription, or the server's copy fell out of sync
 * with what the browser holds (e.g. after a deploy or DB restore).
 */
export async function syncPushSubscription(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await registerSubscription(subscription.toJSON());
    } else {
      await subscribeAndRegister();
    }
  } catch {
    // Best-effort — a failed sync just means we try again on the next load/focus.
  }
}

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'push-subscription-changed' && event.data.subscription) {
      void registerSubscription(event.data.subscription);
    }
  });
}

export function useDisablePush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const subscription = await getExistingSubscription();
      if (!subscription) return;

      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await api.delete('/api/v1/push/subscriptions', { data: { endpoint } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push', 'subscription-status'] });
    },
  });
}
